// filepath: apps/web/src/features/admin/fieldVisibility/FieldVisibilityStateCell.tsx
//
// FR-UI-ADM-4 — the SINGLE switch for the three-state field
// classification presentation (icon + label + `data-state`
// attribute). All consumers (RoleFieldVisibilityTable) MUST render
// via this component — there is no second implementation in the
// fieldVisibility/ subtree, per the AC-7 no-collapse static guard
// contract.
//
// Three independent carriers (per FR-UI-CORE-9 AC-2):
//   1. Icon glyph        — Lucide-react Eye / EyeOff / ShieldOff.
//   2. Text label        — localized via t() (admin.field_visibility.states.*).
//   3. data-state attribute — "visible" | "hidden_cosmetic" | "denied_security".
//
// All three states render distinct content (no collapse). The
// "visible" state is a full row, not a summary (AC-3).
//
// INV-9: no dangerouslySetInnerHTML. Server strings render as JSX
// text children only.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { Locale } from "@/i18n/useTranslation";
import {
  FIELD_VISIBILITY_DATA_STATE,
  type FieldVisibilityState,
} from "./types";

// Inline SVG icon glyphs — no external dependency on lucide-react
// (not installed; constraint says NO new npm deps). The icons are
// tiny (24x24 viewBox) and provide the distinct icon glyph carrier
// the FR-UI-CORE-9 AC-2 contract requires.
//
// Three icons, distinct paths:
//   - Eye         (visible)         — open circle + pupil
//   - EyeOff      (hidden_cosmetic) — open circle + diagonal slash
//   - ShieldOff   (denied_security) — shield outline + diagonal slash
const IconEye = (): React.ReactElement => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeOff = (): React.ReactElement => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const IconShieldOff = (): React.ReactElement => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18" />
    <path d="M4.73 4.73 4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38" />
    <path d="M2 2 22 22" />
  </svg>
);

interface FieldVisibilityStateCellProps {
  /** The field id this row represents. Used in `aria-label` for screen readers. */
  fieldId: string;
  /** The server-resolved classification for this field. */
  classification: FieldVisibilityState;
  /** Active locale. */
  locale: Locale;
}

/**
 * FieldVisibilityStateCell — the single switch for the three
 * states. A static guard in staticGuards.test.ts ensures no other
 * file in apps/web/src/features/admin/fieldVisibility/ collapses
 * these states into the same DOM rendering.
 */
export function FieldVisibilityStateCell({
  fieldId,
  classification,
  locale: _locale,
}: FieldVisibilityStateCellProps): React.ReactElement {
  const { t } = useTranslation();

  // The single switch — three branches, three distinct carriers.
  // Refactor with care: the static guard pins this exact shape.
  switch (classification) {
    case "visible":
      return (
        <span
          className="inline-flex items-center gap-2"
          data-state={FIELD_VISIBILITY_DATA_STATE.visible}
          data-field-id={fieldId}
          data-testid={`field-visibility-cell-${fieldId}`}
          aria-label={t("admin.field_visibility.aria.visible", "visible")}
        >
          <span data-testid={`field-visibility-icon-${fieldId}`}>
            <IconEye />
          </span>
          <span>{t("admin.field_visibility.states.visible", "visible")}</span>
        </span>
      );
    case "hidden_cosmetic":
      return (
        <span
          className="inline-flex items-center gap-2 bg-muted px-2 py-1 rounded"
          data-state={FIELD_VISIBILITY_DATA_STATE.hidden_cosmetic}
          data-field-id={fieldId}
          data-testid={`field-visibility-cell-${fieldId}`}
          aria-label={t(
            "admin.field_visibility.aria.hidden_cosmetic",
            "hidden by visibility formula — cosmetic, server preserves the value",
          )}
        >
          <span data-testid={`field-visibility-icon-${fieldId}`}>
            <IconEyeOff />
          </span>
          <span>
            {t(
              "admin.field_visibility.states.hidden_cosmetic",
              "hidden (cosmetic)",
            )}
          </span>
        </span>
      );
    case "denied_security":
      return (
        <span
          className="inline-flex items-center gap-2 border border-destructive bg-destructive/10 px-2 py-1 rounded"
          data-state={FIELD_VISIBILITY_DATA_STATE.denied_security}
          data-field-id={fieldId}
          data-testid={`field-visibility-cell-${fieldId}`}
          aria-describedby={`field-visibility-tooltip-${fieldId}`}
          aria-label={t(
            "admin.field_visibility.aria.denied_security",
            "denied by permission — security boundary, field is not returned in API responses",
          )}
        >
          <span data-testid={`field-visibility-icon-${fieldId}`}>
            <IconShieldOff />
          </span>
          <span>
            {t(
              "admin.field_visibility.states.denied_security",
              "denied (security)",
            )}
          </span>
          {/* The tooltip id is referenced via aria-describedby above;
              the actual tooltip text lives here as a sr-only span so
              axe-core finds the referenced id in the document. */}
          <span
            id={`field-visibility-tooltip-${fieldId}`}
            className="sr-only"
            data-testid={`field-visibility-tooltip-${fieldId}`}
          >
            {t(
              "admin.field_visibility.tooltip.denied_security_explainer",
              "The server does not return this field — security boundary (FR-AUTH-7).",
            )}
          </span>
        </span>
      );
    default: {
      // The discriminated union guarantees no other branch, but the
      // safe-default returns an unstyled span so an unexpected
      // server payload (e.g. an unrecognised string) does NOT
      // collapse to <HiddenField> which would swallow the
      // distinction. The defensive render is intentional — the
      // AC-7 guard catches any code that maps an unknown state to
      // an existing state silently.
      const exhaustive: never = classification;
      void exhaustive;
      return (
        <span
          className="inline-flex items-center gap-2"
          data-state="unknown"
          data-field-id={fieldId}
          data-testid={`field-visibility-cell-${fieldId}`}
        >
          <span>{String(classification)}</span>
        </span>
      );
    }
  }
}
